import { useEffect, useRef, useState } from 'react';
import { usePresenca } from '../../hooks/usePresenca';
import { emitActivity } from '../../lib/activity';
import { getLocal, onExtensionMessage } from '../../lib/extensionBridge';
import { useScreenText } from '../screen/screenText';
import '../screen/screen.css';

interface CaptureQueueLocalState {
  captureQueueDropped?: unknown;
}

function droppedCount(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

const REPORTED_KEY = 'lm:activity:queueDropped';

/** `capture.queue_dropped` com o que a extensão descartou desde o último registro. */
function reportDropped(total: number): void {
  try {
    const reported = Number(localStorage.getItem(REPORTED_KEY) ?? 0) || 0;
    if (total < reported) localStorage.setItem(REPORTED_KEY, String(total));
    if (total <= reported) return;
    localStorage.setItem(REPORTED_KEY, String(total));
    emitActivity('capture.queue_dropped', { count: total - reported });
  } catch {
    /* storage bloqueado: o aviso na tela continua */
  }
}

/**
 * Torna visível a perda registrada pelo teto da fila persistida da extensão (S3.3), na faixa da
 * proposta "Elements" (17/10/2026), com o que fazer para recuperar.
 * Movimento (23/10/2026): a faixa desce empurrando o conteúdo e recolhe quando zera (.ds-altura no
 * invólucro, que leva as classes do app.css; o texto entra 140 ms depois).
 */
export function CaptureQueueNotice(): JSX.Element | null {
  const { tx, plural } = useScreenText();
  const [dropped, setDropped] = useState(0);

  useEffect(() => {
    let mounted = true;
    const update = (value: unknown) => {
      const next = droppedCount(value);
      if (!mounted || next === null) return;
      reportDropped(next);
      setDropped((current) => (next === 0 ? 0 : Math.max(current, next)));
    };

    void getLocal<CaptureQueueLocalState>()
      .then((local) => update(local.captureQueueDropped))
      .catch(() => {
        // Extensão ausente/legada: o aviso de instalação já cobre esse estado.
      });

    const unsubscribe = onExtensionMessage((message) => {
      if ('captureQueueDropped' in message) update(message['captureQueueDropped']);
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Enquanto recolhe, a faixa segue com o último número (não com o zero que a fez sair).
  const shown = useRef(dropped);
  if (dropped > 0) shown.current = dropped;
  const { montado, aberto } = usePresenca(dropped > 0, '--duracao-200');
  if (!montado) return null;

  return (
    <div
      className={`extension-notice capture-queue-notice as-faixa ds-altura${aberto ? ' is-aberto' : ''}`}
    >
      <div>
        <div className="as-root as-strip ds-altura-conteudo" role="alert" aria-live="polite">
          <span
            className="as-strip__icon as-strip__icon--warn material-symbols-rounded"
            aria-hidden="true"
          >
            warning
          </span>
          <p className="as-strip__text">
            <strong>{tx('drop_title')}</strong> {plural('drop', shown.current)}
          </p>
        </div>
      </div>
    </div>
  );
}
